import { MemberLineRebindControls } from './MemberLineRebindControls';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import type { MemberDto } from '@pokerroom/types';
import { memberCardTokenFromQr } from './memberCardQr';
interface Detector {
  detect(source: HTMLVideoElement): Promise<{ rawValue: string }[]>;
}
export type PortalWork = 'link' | 'unlink' | 'suspend' | 'cases' | 'scan';
export type LineActivity = { busy: boolean; dirty: boolean; dialog?: boolean };
export function MemberPortalControls({
  memberId,
  work,
  onActivity,
  onChanged,
}: {
  memberId: string;
  work?: PortalWork;
  onActivity?: (activity: LineActivity) => void;
  onChanged?: () => void;
}) {
  const actions = useAuthStore((s) => s.allowedActions);
  const navigate = useNavigate();
  const { t } = useTranslation('member');
  const canManage = actions?.includes('member:portal_manage');
  const canRead = actions?.includes('member:read');
  const [code, setCode] = useState('');
  const [pairPurpose, setPairPurpose] = useState<'link' | 'unlink'>(
    work === 'unlink' ? 'unlink' : 'link',
  );
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanKind, setScanKind] = useState<'pairing' | 'card'>('card');
  const [pairingPreview, setPairingPreview] = useState<{
    name: string;
    phone: string;
    memberNo: string;
    expiresAt: string;
  } | null>(null);
  const [identityChecked, setIdentityChecked] = useState(false);
  const [cases, setCases] = useState<
    { id: string; status: string; source: string; createdAt: string; reason?: string }[]
  >([]);
  const [pendingCases, setPendingCases] = useState<
    {
      id: string;
      source: string;
      createdAt: string;
      memberId: string | null;
      requiresOwnerReview: boolean;
    }[]
  >([]);
  const [candidateId, setCandidateId] = useState('');
  const [casesState, setCasesState] = useState<'loading' | 'ready' | 'error'>('loading');
  const mounted = useRef(true);
  const cameraGeneration = useRef(0);
  const sending = useRef(false);
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream>();
  const timer = useRef<number>();
  const active = useRef(false);
  const stop = () => {
    active.current = false;
    cameraGeneration.current++;
    clearTimeout(timer.current);
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = undefined;
    setScanning(false);
  };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cameraGeneration.current++;
      active.current = false;
      clearTimeout(timer.current);
      stream.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);
  useEffect(() => {
    onActivity?.({ busy, dirty: !!(code || reason || candidateId || pairingPreview || scanning) });
  }, [busy, code, reason, candidateId, pairingPreview, scanning, onActivity]);
  useEffect(() => {
    if (canManage && import.meta.env.VITE_MEMBER_PILOT_ENABLED === 'true') void loadCases();
  }, [canManage, memberId]);
  if (
    import.meta.env.VITE_MEMBER_PILOT_ENABLED !== 'true' ||
    (!canManage && !canRead && !actions?.includes('member:line_rebind'))
  )
    return null;
  async function loadCases() {
    setCasesState('loading');
    try {
      const [memberCases, venueCases] = await Promise.all([
        api.get<typeof cases>(`/members/${memberId}/registration-cases`),
        api.get<typeof pendingCases>('/members/registration-cases/pending'),
      ]);
      if (!mounted.current) return;
      setCasesState('ready');
      setCases(memberCases);
      setPendingCases(venueCases);
    } catch {
      if (mounted.current) {
        setCases([]);
        setPendingCases([]);
        setCasesState('error');
      }
    }
  }
  async function manage(kind: 'portal-invitations' | 'portal-suspend') {
    if (sending.current) return;
    if (
      !reason.trim() ||
      (kind === 'portal-invitations' && (!code.trim() || !pairingPreview || !identityChecked))
    ) {
      setMessage(
        t('portalManualReasonRequired', {
          defaultValue: '請先輸入、貼上或掃描配對碼，核對姓名與電話並填寫原因。',
        }),
      );
      return;
    }
    if (
      kind === 'portal-suspend' &&
      !window.confirm(
        t('portalSuspendConfirm', {
          defaultValue: '確定停用此會員的入口權限？本次試辦不提供恢復功能。',
        }),
      )
    )
      return;
    stop();
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    setMessage('');
    try {
      await api.post(
        `/members/${memberId}/${kind === 'portal-invitations' && pairPurpose === 'unlink' ? 'line-unlink/approve' : kind}`,
        {
          reason: reason.trim(),
          ...(kind === 'portal-invitations'
            ? {
                code: code.trim(),
                verifiedName: pairingPreview!.name,
                verifiedPhone: pairingPreview!.phone,
                identityChecked,
              }
            : {}),
        },
      );
      if (!mounted.current) return;
      onChanged?.();
      setCode('');
      setPairingPreview(null);
      setIdentityChecked(false);
      setReason('');
      setMessage(
        kind === 'portal-invitations'
          ? pairPurpose === 'unlink'
            ? '解綁已批准，仍需會員在原裝置確認。'
            : t('portalInviteDone', { defaultValue: '核對已送出，請會員在原裝置確認綁定。' })
          : t('portalSuspendDone', { defaultValue: '會員入口已停用，既有辨識碼立即失效。' }),
      );
      void loadCases();
    } catch {
      if (!mounted.current) return;
      onChanged?.();
      setPairingPreview(null);
      setIdentityChecked(false);
      setMessage(t('portalFailed', { defaultValue: '無法完成，請確認權限、會員狀態及配對碼。' }));
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  async function reportDuplicate() {
    if (!candidateId.trim() || !reason.trim()) return;
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    try {
      await api.post(`/members/${memberId}/registration-cases/report`, {
        candidateMemberId: candidateId.trim(),
        reason: reason.trim(),
      });
      if (!mounted.current) return;
      onChanged?.();
      setCandidateId('');
      setMessage('已建立重複註冊審查案件；會員資料未合併。');
      void loadCases();
    } catch {
      if (!mounted.current) return;
      onChanged?.();
      setPairingPreview(null);
      setIdentityChecked(false);
      setMessage('無法建立案件，請確認兩筆會員資料與場館權限。');
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  async function decide(caseId: string, disposition: 'REFERRED' | 'REJECTED') {
    if (!reason.trim()) {
      setMessage('請填寫處理原因。');
      return;
    }
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    try {
      await api.post(`/members/${memberId}/registration-cases/${caseId}/decision`, {
        disposition,
        reason: reason.trim(),
      });
      if (!mounted.current) return;
      onChanged?.();
      setMessage(
        disposition === 'REFERRED'
          ? '案件已轉交；會員資料未合併。'
          : '案件已拒絕；會員資料未變更。',
      );
      void loadCases();
    } catch {
      if (!mounted.current) return;
      onChanged?.();
      setPairingPreview(null);
      setIdentityChecked(false);
      setMessage('案件狀態或權限已變更，請重新載入。');
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  async function resolve(token: string) {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    stop();
    try {
      const member = await api.post<MemberDto>('/members/portal-card/resolve', { token });
      if (!mounted.current) return;
      navigate(`/members/${member.id}`);
      setMessage(t('portalResolved', { defaultValue: '已辨識會員，未變更點數或報名。' }));
    } catch {
      if (!mounted.current) return;
      setPairingPreview(null);
      setIdentityChecked(false);
      setMessage(
        t('portalCardInvalid', {
          defaultValue: '辨識碼無效或已過期，請重新出示或使用會員編號查詢。',
        }),
      );
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  async function previewPairing(value: string) {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    stop();
    setPairingPreview(null);
    setIdentityChecked(false);
    try {
      const preview = await api.post<{
        name: string;
        phone: string;
        memberNo: string;
        expiresAt: string;
      }>(
        `/members/${memberId}/${pairPurpose === 'unlink' ? 'line-unlink/preview' : 'portal-pairing/preview'}`,
        { code: value },
      );
      if (!mounted.current) return;
      setCode(value);
      setPairingPreview(preview);
      setMessage(
        t('portalPairingReview', {
          defaultValue: '請當面核對會員姓名與電話，再明確批准。掃描本身不會建立連結。',
        }),
      );
    } catch {
      if (!mounted.current) return;
      setPairingPreview(null);
      setIdentityChecked(false);
      setMessage(
        t('portalPairPurposeFailed', {
          defaultValue: '配對碼無效、已過期或用途不符；請確認上方配對用途及目前會員。',
        }),
      );
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  async function scan(kind: 'pairing' | 'card') {
    setMessage('');
    setScanKind(kind);
    const Barcode = (
      window as unknown as { BarcodeDetector?: new (options: { formats: string[] }) => Detector }
    ).BarcodeDetector;
    if (!Barcode || !navigator.mediaDevices?.getUserMedia) {
      setMessage(
        t('portalCameraFallback', { defaultValue: '此裝置無法掃描，請使用會員編號查詢。' }),
      );
      return;
    }
    const cameraTicket = ++cameraGeneration.current;
    active.current = true;
    setScanning(true);
    try {
      const camera = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      if (!active.current || cameraTicket !== cameraGeneration.current) {
        camera.getTracks().forEach((track) => track.stop());
        return;
      }
      stream.current = camera;
      if (!video.current) {
        stop();
        return;
      }
      video.current.srcObject = camera;
      await video.current.play();
      if (!active.current || cameraTicket !== cameraGeneration.current) return;
      const detector = new Barcode({ formats: ['qr_code'] });
      const read = async () => {
        if (!active.current || cameraTicket !== cameraGeneration.current || !video.current) return;
        try {
          const codes = await detector.detect(video.current);
          if (!active.current || cameraTicket !== cameraGeneration.current) return;
          if (codes[0]) {
            if (kind === 'pairing') await previewPairing(codes[0].rawValue);
            else {
              const token = memberCardTokenFromQr(codes[0].rawValue, window.location.origin);
              if (token) await resolve(token);
              else {
                stop();
                setMessage('辨識碼無效，請會員重新出示。');
              }
            }
            return;
          }
        } catch {
          if (!active.current || cameraTicket !== cameraGeneration.current) return;
          stop();
          setMessage(
            t('portalCameraFallback', { defaultValue: '此裝置無法掃描，請使用會員編號查詢。' }),
          );
          return;
        }
        timer.current = window.setTimeout(() => void read(), 250);
      };
      await read();
    } catch {
      if (!active.current || cameraTicket !== cameraGeneration.current) return;
      stop();
      setMessage(
        t('portalCameraFallback', { defaultValue: '此裝置無法掃描，請使用會員編號查詢。' }),
      );
    }
  }
  return (
    <section
      className="mb-5 rounded-lg border border-base-300 bg-base-100 p-4"
      aria-label="會員入口試辦"
    >
      {!work && <MemberLineRebindControls key={memberId} memberId={memberId} />}
      <h2 className="font-semibold">{t('portalTitle', { defaultValue: '會員入口試辦' })}</h2>
      <p className="mt-1 text-sm text-base-content/60">
        {t('portalExplanation', { defaultValue: '先在櫃檯核對本人，再批准原裝置的配對碼。' })}
      </p>
      {canManage && work !== 'scan' && (
        <div className="mt-3 grid gap-3">
          {(!work || work === 'link' || work === 'unlink') && (
            <>
              {!work && (
                <label>
                  配對用途
                  <select
                    disabled={busy || scanning}
                    value={pairPurpose}
                    onChange={(e) => {
                      setPairPurpose(e.target.value as 'link' | 'unlink');
                      setCode('');
                      setPairingPreview(null);
                      setIdentityChecked(false);
                      stop();
                    }}
                  >
                    <option value="link">連結既有會員</option>
                    <option value="unlink">解除此會員的 LINE 綁定（需會員確認）</option>
                  </select>
                  <p className="text-sm">
                    連結碼用於既有會員；解綁碼用於目前已綁定的會員。兩者不能混用。
                  </p>
                </label>
              )}
              <label className="text-sm">
                {t('portalPairingCode', { defaultValue: '配對碼' })}
                <input
                  disabled={busy || scanning}
                  aria-label="配對碼"
                  className="mt-1 block min-h-11 w-full rounded border border-base-300 p-2"
                  value={code}
                  autoComplete="off"
                  onChange={(e) => {
                    setCode(e.target.value);
                    setPairingPreview(null);
                    setIdentityChecked(false);
                  }}
                  placeholder="貼上或手動輸入配對碼"
                  maxLength={80}
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  disabled={busy || scanning}
                  className="min-h-11 rounded border border-base-300 px-4"
                  onClick={() => void scan('pairing')}
                >
                  {t('portalScanPairing', { defaultValue: '掃描會員配對 QR code' })}
                </button>
                <button
                  disabled={busy || !code.trim()}
                  className="min-h-11 underline"
                  onClick={() => void previewPairing(code.trim())}
                >
                  {t('portalReviewPairing', { defaultValue: '檢查配對碼' })}
                </button>
              </div>
              {pairingPreview && (
                <div className="rounded border border-base-300 p-3">
                  <p>
                    {pairingPreview.memberNo} · {pairingPreview.name} · {pairingPreview.phone}
                    <span className="block">
                      有效至 {new Date(pairingPreview.expiresAt).toLocaleTimeString()}
                    </span>
                  </p>
                  <label className="mt-2 flex items-center gap-2">
                    <input
                      type="checkbox"
                      disabled={busy}
                      checked={identityChecked}
                      onChange={(event) => setIdentityChecked(event.target.checked)}
                    />
                    {t('portalIdentityChecked', {
                      defaultValue: '我已當面核對此人的姓名與電話，並確認要連結此會員紀錄。',
                    })}
                  </label>
                </div>
              )}
            </>
          )}
          <label className="text-sm">
            {t('portalReason', { defaultValue: '核對／停用原因' })}
            <input
              aria-label="核對／停用原因"
              className="mt-1 block min-h-11 w-full rounded border border-base-300 p-2"
              disabled={busy}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            {(!work || work === 'link' || work === 'unlink') && (
              <button
                disabled={busy || !identityChecked || !pairingPreview}
                className="min-h-11 rounded bg-primary px-4 text-primary-content"
                onClick={() => void manage('portal-invitations')}
              >
                {pairPurpose === 'unlink'
                  ? '批准解綁，等待會員確認'
                  : t('portalApprove', { defaultValue: '批准配對' })}
              </button>
            )}
            {(!work || work === 'suspend') && (
              <button
                disabled={busy}
                className="min-h-11 rounded border border-error px-4 text-error"
                onClick={() => void manage('portal-suspend')}
              >
                {t('portalSuspend', { defaultValue: '停用會員入口' })}
              </button>
            )}
          </div>
          {(!work || work === 'cases') && (
            <div className="mt-3 rounded border border-base-300 p-3">
              <h3 className="font-medium">重複註冊處理</h3>
              {casesState !== 'ready' && (
                <p role={casesState === 'error' ? 'alert' : 'status'}>
                  {casesState === 'loading'
                    ? t('linePanel.casesLoading')
                    : t('registrationCaseLoadError')}
                </p>
              )}
              {pendingCases.length > 0 && (
                <div className="mt-2 text-sm">
                  <p>本場館待處理案件：{pendingCases.length}</p>
                  {pendingCases.map((item) => (
                    <p key={item.id} className="mt-1">
                      {item.source} · {new Date(item.createdAt).toLocaleString()} ·{' '}
                      {item.requiresOwnerReview
                        ? '候選資料不屬於本場館，保持待處理並轉交資料負責人'
                        : item.memberId === memberId
                          ? '此會員案件，請在下方處理'
                          : '請開啟此場館的候選會員紀錄處理'}
                    </p>
                  ))}
                </div>
              )}
              <p className="text-sm">
                如會員通報另一筆可能重複資料，填入該會員 ID 與原因；系統不會自動合併。
              </p>
              <input
                aria-label="可能重複的會員 ID"
                className="mt-2 w-full rounded border p-2"
                value={candidateId}
                onChange={(event) => setCandidateId(event.target.value)}
              />
              <button
                className="mt-2 min-h-11 rounded border px-4"
                disabled={busy || !candidateId.trim() || !reason.trim()}
                onClick={() => void reportDuplicate()}
              >
                建立審查案件
              </button>
              <h4>{t('linePanel.memberCases')}</h4>
              {cases.map((item) => (
                <div key={item.id} className="mt-2 border-t pt-2 text-sm">
                  <p>
                    {item.source} ·{' '}
                    {t(`registrationCaseStatus.${item.status}`, { defaultValue: item.status })} ·{' '}
                    {new Date(item.createdAt).toLocaleString()}
                  </p>
                  {item.reason && <p>{t('registrationCaseReason', { reason: item.reason })}</p>}
                  {['PENDING', 'APPROVED'].includes(item.status) && (
                    <div className="flex gap-3">
                      <button
                        className="underline"
                        disabled={busy}
                        onClick={() => void decide(item.id, 'REFERRED')}
                      >
                        {t('registrationCaseRefer')}
                      </button>
                      <button
                        className="underline"
                        disabled={busy}
                        onClick={() => void decide(item.id, 'REJECTED')}
                      >
                        {t('registrationCaseReject')}
                      </button>
                    </div>
                  )}
                  {item.status === 'APPROVED' && <p>{t('registrationCaseAwaitingConfirmation')}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {canRead && (!work || work === 'scan') && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            disabled={busy || scanning}
            className="min-h-11 rounded border border-base-300 px-4"
            onClick={() => void scan('card')}
          >
            {t('portalScan', { defaultValue: '掃描會員辨識碼' })}
          </button>
          <Link className="underline" to="/members">
            {t('portalLookup', { defaultValue: '改用會員編號查詢' })}
          </Link>
        </div>
      )}
      {scanning && (
        <div className="mt-3">
          <p>{scanKind === 'pairing' ? '掃描配對 QR code' : '掃描會員辨識碼'}</p>
          <video ref={video} muted playsInline className="max-h-64 w-full rounded" />
          <button className="min-h-11 underline" onClick={stop}>
            {t('portalStopCamera', { defaultValue: '關閉相機' })}
          </button>
        </div>
      )}
      {message && (
        <p className="mt-3 text-sm" role="status">
          {message}
        </p>
      )}
    </section>
  );
}
